import { QueryClient } from "@tanstack/react-query";
import type { UUID } from "@/lib/types";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Server pushes change-events over WS — local cache survives longer between refetches.
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error: unknown) => {
        const status = (error as { status?: number } | null)?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: 0,
    },
  },
});

export const queryKeys = {
  me: ["auth", "me"] as const,
  workspaces: ["workspaces"] as const,
  myBoards: ["my-boards"] as const,
  mySubmissions: ["me", "submissions"] as const,
  notifications: ["me", "notifications"] as const,
  notificationsUnread: ["me", "notifications", "unread-count"] as const,
  pinnedBoards: ["pinned-boards"] as const,
  boards: (workspaceId: string) => ["workspaces", workspaceId, "boards"] as const,
  board: (boardId: string) => ["boards", boardId] as const,
  boardShares: (boardId: string) => ["boards", boardId, "shares"] as const,
  boardStatusSegments: (boardId: string) => ["boards", boardId, "status-segments"] as const,
  rowComments: (rowId: string) => ["rows", rowId, "comments"] as const,
  rowActivity: (rowId: string) => ["rows", rowId, "activity"] as const,
  rowSubscription: (rowId: string) => ["rows", rowId, "subscription"] as const,
  rowShares: (rowId: string) => ["rows", rowId, "shares"] as const,
  memoOrigin: (rowId: string) => ["rows", rowId, "memo-origin"] as const,
  rowLinks: (rowId: string) => ["rows", rowId, "links"] as const,
  sharedTickets: ["me", "shared-tickets"] as const,
  workspaceMembers: (workspaceId: string) => ["workspaces", workspaceId, "members"] as const,
  userSearch: (q: string) => ["users", "search", q] as const,
  assignedCount: ["me", "assigned-count"] as const,
  meetingSeries: ["meeting-series"] as const,
  series: (seriesId: string) => ["meeting-series", seriesId] as const,
  seriesMemos: (seriesId: string) => ["meeting-series", seriesId, "memos"] as const,
  seriesTasks: (id: UUID) => ["meeting-series", id, "tasks"] as const,
  memo: (memoId: string) => ["memos", memoId] as const,
  memoAudit: (memoId: string) => ["memos", memoId, "audit"] as const,
  memoSectionHistory: (memoId: UUID, anchorId: UUID) =>
    ["memos", memoId, "section-history", anchorId] as const,
};
